import { isValidElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Field } from "formik";
import { FilterCollapsiblePanel } from "app/features/product/productFilters/components/FilterCollapsible";
import { Radio } from "app/components/form/Radio";
import CheckboxFormik from "app/pages/.shared/form/CheckboxFormik";
import { FILTER_SELECTION_TYPES } from "../constants/filterSelectionTypes";
import { SDP_PRODUCTS_FILTERS_KEY, HOTEL_ONLY_PRODUCTS_FILTERS_KEY } from "app/constants";
import { TripAdvisor } from "app/icons/brands/TripAdvisor";
import Searchbar from "app/pages/HotelOnly/components/Searchbar/Searchbar";
import PropTypes from "prop-types";
import "./FilterDrawerFormPanel.scss";

const isJSX = value => isValidElement(value);

export const FilterDrawerFormPanel = ({
	filterField,
	options,
	selectedOptions,
	panelTitle,
	selectionMode,
	onFilterValueChange,
	shouldDisplaySearchbar,
	searchbarPlaceholderKey,
	showSelectedFiltersCount = true,
	...props
}) => {
	const { formatMessage } = useIntl();

	const [query, setQuery] = useState("");

	const handleQueryChange = e => {
		setQuery(e.target.value);
	};

	const filteredOptions =
		query?.length > 0
			? options.filter(option =>
					option.label
						.toString()
						.toLowerCase()
						.includes(query.toLowerCase())
			  )
			: options;

	return (
		<FilterCollapsiblePanel
			className="filter-drawer-form-panel"
			title={
				<div className="filter-drawer-form-panel__collapsible-title">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold component="h3">
						{panelTitle}

						{showSelectedFiltersCount &&
							selectedOptions.length > 0 &&
							` (${selectedOptions.length})`}
					</Typography>

					{(filterField === SDP_PRODUCTS_FILTERS_KEY.GUESTS_RATING ||
						filterField === HOTEL_ONLY_PRODUCTS_FILTERS_KEY.TRIPADVISOR) && (
						<TripAdvisor size={24} />
					)}
				</div>
			}
			{...!showSelectedFiltersCount && {
				selectedFilter: selectedOptions[0]?.label,
			}}
			key={filterField}
			{...props}
		>
			{shouldDisplaySearchbar && (
				<Searchbar
					className="filter-drawer-form-panel__searchbar"
					value={query}
					onChange={handleQueryChange}
					placeholder={formatMessage({
						id: searchbarPlaceholderKey || "sdp.search.cta.label",
					})}
				/>
			)}

			{filteredOptions?.length > 0 ? (
				filteredOptions.map(({ value, label, labelPrefix, count }) => {
					if (selectionMode === FILTER_SELECTION_TYPES.SINGLE) {
						return (
							<Radio
								key={String(value)}
								name={filterField}
								value={String(value)}
								className="filter-drawer-form-panel__radio"
								disabled={count === 0}
								onChange={() => {
									onFilterValueChange(filterField, String(value));
								}}
							>
								<div className="filter-drawer-form-panel__radio-content">
									{isJSX(labelPrefix) && labelPrefix}

									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										component="span"
									>
										{label}
									</Typography>

									<Typography
										variant={TYPOGRAPHY_VARIANTS.SMALL}
										component="span"
										className="filter-drawer-form-panel__count-text"
										isBold
									>
										{count}
									</Typography>
								</div>
							</Radio>
						);
					}

					return (
						<Field
							key={String(value)}
							id={String(value)}
							name={filterField}
							component={CheckboxFormik}
							size={16}
							value={String(value)}
							grouped={true}
							className="filter-drawer-form-panel__checkbox"
							onUpdate={(value, field) => {
								onFilterValueChange(field, value);
							}}
							disabled={count === 0}
						>
							{isJSX(labelPrefix) && labelPrefix}

							<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="span">
								{label}
							</Typography>

							<Typography
								variant={TYPOGRAPHY_VARIANTS.SMALL}
								component="span"
								className="filter-drawer-form-panel__count-text"
								isBold
							>
								{count}
							</Typography>
						</Field>
					);
				})
			) : (
				<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="p">
					<FormattedMessage id="faq.search.no.result" />
				</Typography>
			)}
		</FilterCollapsiblePanel>
	);
};

FilterDrawerFormPanel.propTypes = {
	filterField: PropTypes.string.isRequired,
	selectedOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
			count: PropTypes.number,
			labelPrefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		})
	).isRequired,
	panelTitle: PropTypes.string.isRequired,
	selectionMode: PropTypes.oneOf(Object.values(FILTER_SELECTION_TYPES)).isRequired,
	shouldDisplaySearchbar: PropTypes.bool,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
			count: PropTypes.number,
			labelPrefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		})
	).isRequired,
	onFilterValueChange: PropTypes.func.isRequired,
	shouldAutoClose: PropTypes.bool,
	closeAllFacets: PropTypes.func.isRequired,
	searchbarPlaceholderKey: PropTypes.string,
	showSelectedFiltersCount: PropTypes.bool,
};
